import { useEffect, useState, useCallback, Fragment } from 'react';
import { MapPinIcon, BoltIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { Link, useRouter } from '../../util/router';
import { useBrowserType, useShowActions } from '../../hooks';
import { getShowById, getShowsByIDs } from '../../util/util';
import { atcb_action } from 'add-to-calendar-button';
import Map, { Marker, FullscreenControl, GeolocateControl } from 'react-map-gl/maplibre';
import { formatDate, formatTimeRange } from '../../util/data-factory';
import SecondaryShowTile from '../../components/SecondaryShowTile';
import HomeFooter from '../../components/HomeFooter';
import Meta from '../../components/Meta';
import ShowImagePlaceholder from '../../components/ShowImagePlaceholder';
import ShowActionButtons from '../../components/ShowActionButtons';
import PageHeader from '../../components/shell/PageHeader';

function VenueName({ venue }) {
  if (!venue) return null;
  return venue.org_id ? (
    <Link to={`/org/${venue.org_id}`} className="hover:underline">
      {venue.venue_name}
    </Link>
  ) : (
    <>{venue.venue_name}</>
  );
}

const ShowPage = () => {
  const router = useRouter();
  const browser = useBrowserType();
  const { id } = router.query;
  const [showInfo, setShowinfo] = useState();
  const [expandedImageIndex, setExpandedImageIndex] = useState(null);
  const { isSeen, isSaved, handleSeen, handleSave, modals } = useShowActions();
  const [similarShows, setSimilarShows] = useState([]);
  const [nearbyShows, setNearbyShows] = useState([]);

  const fetchShowByID = useCallback(async () => {
    try {
      const show = await getShowById(id);
      setShowinfo(show[0]);

      const similar = await getShowsByIDs(show[0].similar_shows);
      const nearby = await getShowsByIDs(show[0].nearby_shows);
      setSimilarShows(similar);
      setNearbyShows(nearby);
    } catch (error) {
      console.error(error);
    }
  }, [id]);

  // ********************************* //
  // ************ Effects ************ //
  // ********************************* //

  useEffect(() => {
    if (id) {
      fetchShowByID();
    }
  }, [id, fetchShowByID]);

  // ********************************* //
  // ******* Internal Funcs ********** //
  // ********************************* //

  const formatTags = () => {
    if (!showInfo) {
      return '';
    }
    const { show_style, show_medium, show_subject_matter, firs_ny_solo, female_astist } = showInfo;
    const display = [...(show_style ?? []), ...(show_medium ?? []), ...(show_subject_matter ?? [])].join(' | ');
    return `${firs_ny_solo ? 'First NY Solo |' : ''}${
      female_astist ? 'Female Artist |' : ''
    } ${display}`;
  };

  const formatArtistNames = () => {
    if (!showInfo) {
      return '';
    }
    return showInfo.artist_names.length > 1
      ? showInfo.artist_names.join(', ')
      : showInfo.artist_names[0];
  };

  const today = new Date().toISOString().split('T')[0];

  const galleryImageUrls = Array.isArray(showInfo?.image_urls)
    ? showInfo.image_urls.filter(u => typeof u === 'string' && u.trim().length > 0)
    : [];

  const config = {
    name: `Showrunner - ${showInfo?.display_title}`,
    description: `Remind me about this art show:[br]→ [url]${window.location.href}/|Click here![/url]`,
    startDate: `${showInfo?.start_date}`,
    endDate: `${showInfo?.end_date}`,
    options: [`${browser === 'Safari' ? 'Apple' : 'Google'}`], // 'Apple' or 'Google' based on browser
    timeZone: 'America/New_York',
    location: `${showInfo?.venues.venue_name}, ${showInfo?.venues.city}`,
  };

  /**
   * Handle the click event on the date section of the show
   * This will trigger the add-to-calendar-button action
   */
  const handleCalClick = () => {
    const button = document.getElementById('cal-button');
    if (button) {
      atcb_action(config, button);
    }
  };

  return (
    <Fragment>
      <Meta title="Show" />
      <PageHeader back />
      <section className="w-full overflow-hidden bg-slate-100">
        <div className="flex w-full h-full p-4 md:p-8 my-auto mx-auto overflow-auto">
          <div className="w-full content-end">
            <h1 className="text-2xl md:text-4xl font-medium">{showInfo?.display_title ?? showInfo?.show_title}</h1>
            <p className="text-lg md:text-xl text-black/70 font-medium">{formatArtistNames()}</p>
            <p className="text-secondary-red text-sm md:text-lg">{formatTags()}</p>

            <div className="flex justify-between">
              <div className="flex justify-between text-xs md:text-sm my-3">
                <div className="flex flex-1 flex-wrap">
                  <div className="flex mr-4 items-center">
                    <MapPinIcon className="w-5" /> <VenueName venue={showInfo?.venues} />
                  </div>
                  <div className="flex mr-4 items-center" id="cal-button" onClick={handleCalClick}>
                    <CalendarIcon className="w-5 mr-1" /> {formatDate(showInfo?.start_date, true)} -{' '}
                    {formatDate(showInfo?.end_date, true)}
                  </div>
                  {showInfo?.recommended ? (
                    <div className="flex mr-4 items-center">
                      <BoltIcon className="w-5" /> Trending
                    </div>
                  ) : (
                    ''
                  )}
                </div>
              </div>
              <div className="my-auto flex items-center gap-2">
                <ShowActionButtons
                  showId={Number(id)}
                  showTitle={showInfo?.display_title}
                  isSeen={isSeen(Number(id))}
                  isSaved={isSaved(Number(id))}
                  onSeenClick={() => void handleSeen(Number(id))}
                  onSaveClick={() => handleSave(Number(id))}
                />
              </div>
            </div>
            {showInfo?.opening_date && (
              <div className="flex normal-case text-xs md:text-sm">
                <p className="mr-2">Opening Reception: </p>
                <p className="mr-2 text-black/50">{formatDate(showInfo.opening_date, true)}</p>
                <p className="text-black/50">
                  {formatTimeRange(showInfo.opening_start_time, showInfo.opening_end_time)}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="py-1">
        {galleryImageUrls.length ? (
          <div
            className={`grid grid-flow-col auto-cols-max w-full p-4 md:p-8 gap-5 overflow-auto items-center ${
              galleryImageUrls.length > 1 ? 'justify-start' : 'justify-center'
            }`}
          >
            {galleryImageUrls.map((url, index) => (
              <div className="mx-auto cursor-pointer" key={index}>
                {index === expandedImageIndex ? (
                  <div className="fixed top-0 left-0 bg-white w-full h-full flex items-center justify-center z-50">
                    <img
                      src={url}
                      alt="show_image"
                      className="h-screen w-auto md:h-full object-contain"
                      onClick={() => setExpandedImageIndex(null)}
                    />
                  </div>
                ) : (
                  <img
                    className="min-w-full m-auto max-h-[220px] md:max-h-[390px]"
                    src={url}
                    alt="show_image"
                    onClick={() => setExpandedImageIndex(index)}
                  />
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="w-full max-w-[90rem] mx-auto p-4 md:p-8">
            <ShowImagePlaceholder />
          </div>
        )}
      </div>

      <section className="w-full mt-1 overflow-hidden bg-secondary">
        <div className="flex w-full h-full max-w-[90rem] p-4 md:px-8 md:py-2 my-auto mx-auto">
          <div className="grid grid-cols-3 md:grid-cols-4 gap-4 border-b border-slate-200 pb-4">
            <div className="col-span-3 md:col-span-3 my-auto">
              {showInfo?.long_description && (
                <div>
                  <h3 className="text-lg md:text-2xl mb-1">Intro</h3>
                  <p className="text-md sm:text-lg pr-2 text-black/60">
                    {showInfo?.long_description}
                  </p>
                </div>
              )}
              {showInfo?.link_out && (
                <a
                  href={showInfo?.link_out}
                  className="text-md sm:text-lg pr-2 text-secondary-red underline"
                >
                  {showInfo?.link_out_text || 'See the online viewing room'}
                </a>
              )}
            </div>
            <div className="col-span-2 md:col-auto md:mr-auto">
              <h3 className="text-lg md:text-2xl">Location</h3>
              <div className="overflow-auto text-black/60 px-4 md:px-0">
                <p>
                  <VenueName venue={showInfo?.venues} />
                </p>
                {showInfo?.venues?.org_id && (
                  <Link
                    to={`/org/${showInfo.venues.org_id}`}
                    className="text-sm text-secondary-red underline"
                  >
                    More from this venue
                  </Link>
                )}
                {showInfo?.show_type !== 'gallery' && <p>{showInfo?.show_type}</p>}
                <div className="flex">
                  <p className="mr-2">{showInfo?.venues.neighborhood},</p>
                  <p>{showInfo?.venues.city}</p>
                </div>
                <br />
                <div className="flex">
                  <p className="mr-2">{showInfo?.venues.address1}</p>
                  <p>{showInfo?.venues.phone}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-[90rem] px-4 md:px-8 md:py-2 my-auto mx-auto">
          <h3 className="text-lg md:text-2xl mb-2">Similar Shows</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-2 md:px-8 w-full border-b border-slate-200">
            {similarShows.length > 0 && (
              <Fragment>
                <div className="flex flex-col">
                  {similarShows.slice(0, 2).map(show => (
                    <SecondaryShowTile key={show.show_id} show={show} today={today} />
                  ))}
                </div>

                <div className="flex flex-col">
                  {similarShows.slice(2, 4).map(show => (
                    <SecondaryShowTile key={show.show_id} show={show} today={today} />
                  ))}
                </div>

                <div className="flex flex-col">
                  {similarShows.slice(4, 6).map(show => (
                    <SecondaryShowTile key={show.show_id} show={show} today={today} />
                  ))}
                </div>
              </Fragment>
            )}
          </div>
        </div>

        {showInfo?.show_type === 'Gallery' && (
          <div className="max-w-[90rem] px-4 md:px-8 md:py-2 my-auto mx-auto">
            <h3 className="text-lg md:text-2xl my-2">Nearby Shows</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-2 md:px-8 w-full">
              {nearbyShows.length > 0 && (
                <Fragment>
                  <div className="flex flex-col">
                    {nearbyShows.slice(0, 2).map(show => (
                      <SecondaryShowTile key={show.show_id} show={show} today={today} />
                    ))}
                  </div>

                  <div className="flex flex-col">
                    {nearbyShows.slice(2, 4).map(show => (
                      <SecondaryShowTile key={show.show_id} show={show} today={today} />
                    ))}
                  </div>

                  <div className="flex flex-col">
                    {nearbyShows.slice(4, 6).map(show => (
                      <SecondaryShowTile key={show.show_id} show={show} today={today} />
                    ))}
                  </div>
                </Fragment>
              )}
            </div>
          </div>
        )}
      </section>
      {/*<div className="rounded-xl basis-2/3 overflow-hidden">
            {latitude && longitude && <VenueMap latitude={latitude} longitude={longitude} />}
          </div> 
        */}
      {modals}
      <HomeFooter hideMobile />
    </Fragment>
  );
};

export default ShowPage;

// eslint-disable-next-line no-unused-vars
function VenueMap({ latitude, longitude }) {
  return (
    <Map
      initialViewState={{
        longitude: longitude ?? -74.004,
        latitude: latitude ?? 40.748,
        zoom: 15,
      }}
      center={[latitude, longitude]}
      maxZoom={19}
      style={{ width: 220, height: 280 }}
      mapStyle="https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json"
      maplibreLogo={false}
    >
      <GeolocateControl />
      <Marker longitude={longitude} latitude={latitude} anchor="center" />
      <FullscreenControl position="top-left" />
    </Map>
  );
}
